// frontend/src/hooks/useUsageNotifications.js
// Smart usage notifications and warnings system

import { useCallback, useEffect, useState } from 'react';
import { useSubscription } from './useSubscription';

const useUsageNotifications = () => {
  const { usage, subscription, isPro, getUsagePercentage } = useSubscription();
  const [shownNotifications, setShownNotifications] = useState(new Set());
  const [activeNotification, setActiveNotification] = useState(null);

  // Progressive engagement: Check for usage warnings with improved UX thresholds
  const checkUsageWarnings = useCallback(() => {
    if (!usage || !subscription || isPro()) {
      return; // No warnings for pro users or when data not loaded
    }

    const dailyKey = new Date().toDateString(); // Only show once per day
    const monthlyKey = `${new Date().getFullYear()}-M${new Date().getMonth()}`; // Monthly for milestone notifications

    // Global cooldown: Max 1 upgrade nudge per 48 hours (except critical 95%+)
    const COOL_HOURS = 48;
    const now = Date.now();
    const lastUpgradeNudge = typeof window !== 'undefined'
      ? Number(localStorage.getItem('lastUpgradeNudge') || 0)
      : 0;
    const inCooldown = (now - lastUpgradeNudge) < COOL_HOURS * 3600 * 1000;

    // Helper function to get the highest usage percentage
    const getHighestUsage = () => {
      const voicePercent = getUsagePercentage(usage.voice?.minutesUsed || 0, usage.voice?.minutesLimit || 0);
      const aiPercent = getUsagePercentage(usage.aiOperations?.used || 0, usage.aiOperations?.limit || 0);
      const storagePercent = getUsagePercentage(usage.storage?.bytesUsed || 0, usage.storage?.bytesLimit || 0);
      
      if (voicePercent >= aiPercent && voicePercent >= storagePercent) {
        return { type: 'voice', percent: voicePercent, name: 'voice minutes' };
      } else if (aiPercent >= storagePercent) {
        return { type: 'ai', percent: aiPercent, name: 'AI operations' };
      } else {
        return { type: 'storage', percent: storagePercent, name: 'storage space' };
      }
    };

    const highestUsage = getHighestUsage();
    const percent = highestUsage.percent;

    // 95%+ CRITICAL — exempt from cooldown, daily
    if (percent >= 95 && !shownNotifications.has(`critical_${highestUsage.type}_${dailyKey}`)) {
      const notification = {
        id: `critical_${highestUsage.type}_${dailyKey}`,
        type: 'error',
        title: 'Upgrade Now to Continue',
        message: `You've nearly reached your monthly limit (${Math.round(percent)}% of ${highestUsage.name}).`,
        action: {
          label: 'Upgrade Instantly',
          url: '/upgrade'
        }
      };
      showNotification(notification);
      setShownNotifications(prev => new Set([...prev, notification.id]));
      if (typeof window !== 'undefined') {
        localStorage.setItem('lastUpgradeNudge', String(now));
      }
      return;
    }

    // Respect cooldown for all non-critical nudges
    if (inCooldown) return;

    // 85-94% URGENT — daily
    if (percent >= 85 && percent < 95 && !shownNotifications.has(`urgent_${highestUsage.type}_${dailyKey}`)) {
      const notification = {
        id: `urgent_${highestUsage.type}_${dailyKey}`,
        type: 'warning',
        title: 'Upgrade Now to Avoid Interruptions',
        message: `You're close to your monthly limit (${Math.round(percent)}% of ${highestUsage.name}).`,
        action: {
          label: 'Upgrade to Pro',
          url: '/upgrade'
        }
      };
      showNotification(notification);
      setShownNotifications(prev => new Set([...prev, notification.id]));
      if (typeof window !== 'undefined') {
        localStorage.setItem('lastUpgradeNudge', String(now));
      }
      return;
    }

    // 70-84% NOTICE — daily
    if (percent >= 70 && percent < 85 && !shownNotifications.has(`notice_${highestUsage.type}_${dailyKey}`)) {
      const notification = {
        id: `notice_${highestUsage.type}_${dailyKey}`,
        type: 'info',
        title: 'Consider Upgrading Soon',
        message: `You're approaching your monthly limit (${Math.round(percent)}% of ${highestUsage.name}).`,
        action: {
          label: 'See Pro Benefits',
          url: '/upgrade'
        }
      };
      showNotification(notification);
      setShownNotifications(prev => new Set([...prev, notification.id]));
      if (typeof window !== 'undefined') {
        localStorage.setItem('lastUpgradeNudge', String(now));
      }
      return;
    }

    // 50-69% MILESTONE — monthly (less chatty than weekly)
    if (percent >= 50 && percent < 70 && !shownNotifications.has(`milestone_${highestUsage.type}_${monthlyKey}`)) {
      const notification = {
        id: `milestone_${highestUsage.type}_${monthlyKey}`,
        type: 'info',
        title: 'You\'ve Passed the Halfway Point',
        message: `You've used ${Math.round(percent)}% of your ${highestUsage.name} this month. Great progress!`,
        action: {
          label: 'Learn About Pro',
          url: '/upgrade'
        }
      };
      showNotification(notification);
      setShownNotifications(prev => new Set([...prev, notification.id]));
      if (typeof window !== 'undefined') {
        localStorage.setItem('lastUpgradeNudge', String(now));
      }
      return;
    }
  }, [usage, subscription, isPro, getUsagePercentage, shownNotifications]);

  // Show a notification
  const showNotification = useCallback((notification) => {
    setActiveNotification(notification);
    setShownNotifications(prev => new Set([...prev, notification.id]));
    
    // Auto-hide timing based on urgency (more time for critical messages)
    const timeout = {
      'error': 20000,    // Critical messages stay longer
      'warning': 15000,  // Important warnings
      'info': 8000,      // Info messages dismiss quickly
      'success': 6000    // Success messages dismiss fastest
    }[notification.type] || 10000;
    setTimeout(() => {
      setActiveNotification(current => 
        current?.id === notification.id ? null : current
      );
    }, timeout);
  }, []);

  // Manual notification dismissal
  const dismissNotification = useCallback(() => {
    setActiveNotification(null);
  }, []);

  // Show custom notification
  const notify = useCallback((type, title, message, action = null) => {
    const notification = {
      id: `custom_${Date.now()}`,
      type,
      title,
      message,
      action
    };
    showNotification(notification);
  }, [showNotification]);

  // Check usage warnings when data changes
  useEffect(() => {
    if (usage && subscription) {
      const timer = setTimeout(checkUsageWarnings, 1000); // Delay to avoid spam
      return () => clearTimeout(timer);
    }
  }, [usage, subscription, checkUsageWarnings]);

  // Show upgrade success notification
  const showUpgradeSuccess = useCallback(() => {
    notify('success', 'Welcome to Pro!', 'All limits have been removed. Enjoy unlimited access to all features.');
  }, [notify]);

  // Show limit reached notification
  const showLimitReached = useCallback((feature, details) => {
    const messages = {
      voice: `You've reached your voice recording limit (${details.used}/${details.limit} minutes).`,
      ai: `You've reached your AI operations limit (${details.used}/${details.limit} operations).`,
      storage: 'You\'ve reached your storage limit.'
    };

    notify('error', 'Limit Reached', messages[feature] || 'Feature limit reached.', {
      label: 'Upgrade Now',
      url: '/upgrade'
    });
  }, [notify]);

  return {
    activeNotification,
    dismissNotification,
    notify,
    showUpgradeSuccess,
    showLimitReached
  };
};

export default useUsageNotifications;